"use client";

// Die Navigation der Voice-Seiten — in der Markenwelt des Produkts, nicht
// die des Netzwerks: Blatt und Wortmarke links, die Abschnitte der Seite in
// der Mitte mit einem Läufer, der unter dem gerade sichtbaren Abschnitt
// steht, rechts der Weg zurück ins Netzwerk und der Download.
//
// Ganz oben liegt die Leiste ohne Fläche auf der Seite; sobald gescrollt
// wird, zieht sie sich zu einer Glaskapsel zusammen. Auf schmalen Displays
// bleiben Marke und Download stehen, die Abschnitte wandern in ein Blatt,
// das unter der Leiste aufklappt.
import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, Download, Menu, X } from "lucide-react";

const ABSCHNITTE = [
  { id: "buehne", label: "So läuft es" },
  { id: "merkmale", label: "Was drinsteckt" },
  { id: "vertrauen", label: "Sicher & schnell" },
];

// Eigene Seiten, keine Abschnitte: sie haben keinen Läufer und stehen auf
// breiten Displays rechts neben dem Weg ins Netzwerk, auf schmalen im Blatt.
// Die Erweiterungen waren ein Abschnitt und sind jetzt eine Seite.
const SEITEN = [
  { href: "/voice/erweiterungen", label: "Erweiterungen" },
  { href: "/voice/changelog", label: "Changelog" },
  { href: "/voice/hilfe", label: "Hilfe" },
];

export default function VoiceNav({ zuhause = true }: { zuhause?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [aktiv, setAktiv] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);
  const [laeufer, setLaeufer] = useState<{ left: number; width: number } | null>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const blattRef = useRef<HTMLDivElement>(null);

  // Anker: auf der Seite selbst reicht die Raute, von der Datenschutzseite
  // aus muss der Weg über /voice führen.
  const ziel = (id: string) => (zuhause ? `#${id}` : `/voice#${id}`);

  useEffect(() => {
    const onScroll = () => {
      const next = window.scrollY > 24;
      setScrolled((prev) => {
        if (prev !== next) setOffen(false);
        return next;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Welcher Abschnitt ist dran? Der, dessen Fläche die Mitte des
  // Sichtbereichs schneidet — das Fenster ist auf einen schmalen Streifen
  // um die Mitte verengt, damit immer nur einer trifft.
  useEffect(() => {
    if (!zuhause) return;
    const ziele = ABSCHNITTE.map((a) => document.getElementById(a.id)).filter(Boolean) as HTMLElement[];
    if (!ziele.length) return;
    const io = new IntersectionObserver(
      (eintraege) => {
        for (const e of eintraege) if (e.isIntersecting) setAktiv(e.target.id);
        if (window.scrollY < 200) setAktiv(null);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    ziele.forEach((z) => io.observe(z));
    return () => io.disconnect();
  }, [zuhause]);

  // Der Läufer unter dem aktiven Link — Lage und Breite vom Link selbst.
  const messen = useCallback(() => {
    const wrap = linksRef.current;
    if (!wrap || !aktiv) {
      setLaeufer(null);
      return;
    }
    const a = wrap.querySelector<HTMLElement>(`[data-id="${aktiv}"]`);
    if (!a) return;
    setLaeufer({ left: a.offsetLeft, width: a.offsetWidth });
  }, [aktiv]);
  useEffect(() => {
    messen();
    window.addEventListener("resize", messen);
    return () => window.removeEventListener("resize", messen);
  }, [messen]);

  // Blatt schließen per Esc und per Klick außerhalb.
  const schliessen = useCallback((fokus = false) => {
    setOffen(false);
    if (fokus) knopfRef.current?.focus();
  }, []);
  useEffect(() => {
    if (!offen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") schliessen(true);
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (blattRef.current?.contains(t) || knopfRef.current?.contains(t)) return;
      schliessen();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [offen, schliessen]);

  return (
    <header className={`vn${scrolled ? " vn--scrolled" : ""}${offen ? " vn--offen" : ""}`}>
      <nav className="vn-bar" aria-label="Navigation Logsy Voice">
        <Link className="vn-brand" href={zuhause ? "#top" : "/voice"} onClick={() => schliessen()}>
          <Image src="/voice/leaf-light.png" alt="" width={34} height={34} />
          <span>
            Logsy <b>Voice</b>
          </span>
        </Link>

        <div className="vn-links" ref={linksRef}>
          {laeufer && (
            <span
              className="vn-laeufer"
              style={{ left: laeufer.left, width: laeufer.width }}
              aria-hidden="true"
            />
          )}
          {ABSCHNITTE.map((a) => (
            <a
              key={a.id}
              href={ziel(a.id)}
              data-id={a.id}
              aria-current={aktiv === a.id ? "location" : undefined}
            >
              {a.label}
            </a>
          ))}
        </div>

        <div className="vn-right">
          <Link className="vn-seite" href="/voice/erweiterungen">
            Erweiterungen
          </Link>
          <Link className="vn-seite" href="/voice/hilfe">
            Hilfe
          </Link>
          <Link className="vn-net" href="/">
            Logsy Network
            <ArrowUpRight aria-hidden="true" />
          </Link>
          <a className="vn-cta" href={ziel("holen")}>
            <Download aria-hidden="true" />
            <span>Herunterladen</span>
          </a>
          <button
            ref={knopfRef}
            className="vn-knopf"
            type="button"
            aria-expanded={offen}
            aria-controls="vnBlatt"
            aria-label={offen ? "Menü schließen" : "Menü öffnen"}
            onClick={() => setOffen((o) => !o)}
          >
            {offen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </nav>

      {/* ---- Das Blatt für schmale Displays ---- */}
      <div className="vn-blatt" id="vnBlatt" ref={blattRef} aria-hidden={!offen} inert={!offen}>
        <div className="vn-blatt-in">
          {ABSCHNITTE.map((a, i) => (
            <a
              key={a.id}
              href={ziel(a.id)}
              style={{ "--i": i } as React.CSSProperties}
              aria-current={aktiv === a.id ? "location" : undefined}
              onClick={() => schliessen()}
            >
              {a.label}
            </a>
          ))}
          <a href={ziel("holen")} style={{ "--i": 4 } as React.CSSProperties} onClick={() => schliessen()}>
            Herunterladen
          </a>
          {SEITEN.map((s, i) => (
            <Link
              key={s.href}
              href={s.href}
              style={{ "--i": 5 + i } as React.CSSProperties}
              onClick={() => schliessen()}
            >
              {s.label}
            </Link>
          ))}
          <Link href="/" className="vn-blatt-net" style={{ "--i": 5 + SEITEN.length } as React.CSSProperties}>
            Zurück zu Logsy Network
            <ArrowUpRight aria-hidden="true" />
          </Link>
        </div>
      </div>
    </header>
  );
}
